import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerGetPolicyQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
} from 'korint-sdk';

export function useHasRequiredPolicyholderDocuments(): boolean {
  const { policyId, branchId } = useAssertedParams<{ policyId: string; branchId: string }>();

  const { data: productId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.productId,
  });

  // Stays undefined (gate closed) until the config loads; `[]` would pass vacuously
  const { data: requiredCategories } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId ?? ''),
    enabled: !!productId,
    select: data =>
      data.policyholderDocuments.filter(({ required }) => required).map(({ category }) => category),
  });

  // Same key as DocumentsUploadPolicyModule, whose uploads invalidate it
  const { data: uploadedCategories } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(policyId, {}),
    select: data =>
      new Set(
        data.data
          .filter(({ type }) => type === 'supporting_document')
          .map(({ category }) => category),
      ),
  });

  return (
    !!requiredCategories &&
    !!uploadedCategories &&
    requiredCategories.every(category => uploadedCategories.has(category))
  );
}
